import { lazy, Suspense } from 'react';
import { Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { Layout } from './components/Layout.tsx';
import { LoginScreen } from './screens/Login/LoginScreen.tsx';
import { useAuth } from './lib/authStore.tsx';
import { strings } from './lib/strings.tsx';
import { AcceptInviteScreen, ACCEPT_PATH } from './screens/Access/index.tsx';

/*
 * **로그인 뒤 화면은 전부 늦게 싣는다** (지시185).
 *
 * 〔실측 2026-09-14〕 `PipelineBuilderScreen` 하나만 늦게 싣고 나머지 아홉이 첫 조각에 들어가
 * `index-*.js` 가 **572KB** 였고 `vite` 가 빌드마다 «500 kB 넘는 조각» 경고를 냈다 —
 * Runs 만 보려는 사람도 전부를 받았다.
 *
 * ⚠ 로그인·초대 수락은 **그대로 둔다** — 계정 없는 사람이 처음 받는 화면이라 한 번 더 기다리게 하지 않는다.
 * ⚠ 새 화면을 여기 정적 `import` 로 더하면 `everyScreenLoadsLazily` 가 문다.
 */
const PipelineBuilderScreen = lazy(() =>
  import('./screens/PipelineBuilder/PipelineBuilderScreen.tsx').then((m) => ({
    default: m.PipelineBuilderScreen,
  })),
);
const RunsScreen = lazy(() => import('./screens/Runs/RunsScreen.tsx').then((m) => ({ default: m.RunsScreen })));
const AgentIoScreen = lazy(() =>
  import('./screens/AgentIo/AgentIoScreen.tsx').then((m) => ({ default: m.AgentIoScreen })),
);
const ApprovalsScreen = lazy(() =>
  import('./screens/Approvals/ApprovalsScreen.tsx').then((m) => ({ default: m.ApprovalsScreen })),
);
const FleetScreen = lazy(() => import('./screens/Fleet/FleetScreen.tsx').then((m) => ({ default: m.FleetScreen })));
const OperatorScreen = lazy(() =>
  import('./screens/Operator/OperatorScreen.tsx').then((m) => ({ default: m.OperatorScreen })),
);
const SessionsScreen = lazy(() =>
  import('./screens/Sessions/SessionsScreen.tsx').then((m) => ({ default: m.SessionsScreen })),
);
const SettingsScreen = lazy(() =>
  import('./screens/Settings/SettingsScreen.tsx').then((m) => ({ default: m.SettingsScreen })),
);
const ResourcesScreen = lazy(() =>
  import('./screens/Resources/ResourcesScreen.tsx').then((m) => ({ default: m.ResourcesScreen })),
);
const DashboardScreen = lazy(() =>
  import('./screens/Dashboard/DashboardScreen.tsx').then((m) => ({ default: m.DashboardScreen })),
);
const AccessScreen = lazy(() =>
  import('./screens/Access/AccessScreen.tsx').then((m) => ({ default: m.AccessScreen })),
);

const DatabasesScreen = lazy(() =>
  import('./screens/Databases/DatabasesScreen.tsx').then((m) => ({ default: m.DatabasesScreen })),
);
const AgentDefSettingsScreen = lazy(() =>
  import('./screens/AgentDefSettings/AgentDefSettingsScreen.tsx').then((m) => ({ default: m.AgentDefSettingsScreen })),
);

export function App() {
  // 로그인 여부는 GET /v1/auth/me 로만 판정한다(결정46) — 쿠키는 httpOnly라 JS가
  // 직접 읽을 수 없다. 판정 전('checking')에는 아무 화면도 그리지 않아 미인증
  // 상태로 잠깐 콘솔이 보였다가 로그인 화면으로 튕기는 깜빡임을 막는다.
  const { status } = useAuth();
  /*
   * **라우터에게 묻는다.** 종전에는 `window.location` 을 직접 읽었다 — 실기에서는
   * 맞지만 라우터의 상태와 갈라져 있어, 클라이언트 이동에 반응하지 않고 검사에서도
   * 재현할 수 없었다(그래서 이 분기가 검사 밖에 있었다).
   */
  const location = useLocation();

  /*
   * 초대 수락은 **인증 관문 앞에 둔다** (지시54, 통합 브리프 ④).
   *
   * 아직 계정이 없는 사람이 쓰는 화면이다. 관문 뒤에 두면 로그인하라고 튕기는데,
   * 로그인할 계정이 없어서 여기 온 사람이다. `status` 를 아예 안 본다 — 이미 다른
   * 계정으로 로그인해 둔 브라우저에서 초대 링크를 열 수도 있다.
   */
  if (location.pathname === ACCEPT_PATH) {
    return <AcceptInviteScreen search={location.search} />;
  }

  if (status === 'checking') {
    return <div style={{ padding: 24 }}>{strings.app.loading}</div>;
  }
  if (status === 'anonymous') {
    return <LoginScreen />;
  }

  return (
    <Suspense fallback={<div style={{ padding: 24 }}>{strings.app.loading}</div>}>
    <Routes>
      {/* 독립 창 — 틀(옆 메뉴) 밖이다. 창 전체가 설정이다. */}
      <Route path="/agent-defs/:agentDefId/settings" element={<AgentDefSettingsScreen />} />
      <Route element={<Layout />}>
        {/* 지시188 — 로그인 뒤 첫 화면은 Dashboard 다(종전 Runs). */}
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<DashboardScreen />} />
        <Route path="/pipelines" element={<PipelineBuilderScreen />} />
        <Route path="/pipelines/:name" element={<PipelineBuilderScreen />} />
        <Route path="/runs" element={<RunsScreen />} />
        <Route path="/runs/:runId" element={<RunsScreen />} />
        <Route path="/agent-io" element={<AgentIoScreen />} />
        <Route path="/approvals" element={<ApprovalsScreen />} />
        <Route path="/fleet" element={<FleetScreen />} />
        <Route path="/databases" element={<DatabasesScreen />} />
        <Route path="/databases/:serverId" element={<DatabasesScreen />} />
        {/* 지시160 — 도는 사슬을 컨베이어 벨트처럼 보는 자리. */}
        <Route path="/operator" element={<OperatorScreen />} />
        <Route path="/sessions" element={<SessionsScreen />} />
        <Route path="/settings" element={<SettingsScreen />} />
        <Route path="/resources" element={<ResourcesScreen />} />
        <Route path="/access" element={<AccessScreen />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Route>
    </Routes>
    </Suspense>
  );
}
